/**
 * ============================================================================
 * 🌐 API Service — HTTP-Client für den eigenen Server
 * ============================================================================
 * 
 * Zentraler HTTP-Client mit:
 * - JWT-Token-Management (localStorage)
 * - Automatischer Token-Refresh bei 401
 * - Retry-Logik bei Netzwerkfehlern
 * - Timeout-Handling
 * - Convenience-Methoden (get, post, put, patch, delete)
 * 
 * Verwendung:
 *   import { api, loginWithGoogle, isLoggedIn } from './apiService';
 *   
 *   // Login
 *   const session = await loginWithGoogle(idToken, accessToken, refreshToken);
 *   
 *   // API-Calls
 *   const packages = await api.get('/packages');
 *   await api.post('/packages', { absender: 'DHL', empfaenger: 'Max' });
 *   await api.put('/packages/123', { status: 'abgeholt' });
 *   await api.delete('/packages/123');
 * ============================================================================
 */

import { SERVER_URL, TOKEN_KEYS, REQUEST_TIMEOUT } from '../config/serverConfig';
import { reportError } from './errorReport'; // 🧯 erzwungene Abmeldungen fürs Admin-Board
import { tGlobal } from '../utils/tGlobal';

// ============================================================
// Token-Management (localStorage)
// ============================================================

/**
 * JWT Access Token lesen
 */
export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
}

/**
 * JWT Refresh Token lesen
 */
export function getRefreshToken() {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
}

/**
 * Gespeicherte User-Daten lesen
 */
export function getStoredUser() {
  try {
    const data = localStorage.getItem(TOKEN_KEYS.USER_DATA);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

/**
 * Tokens + User-Daten speichern
 */
export function setTokens(accessToken, refreshToken, user, expiresIn) {
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
  if (refreshToken) {
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
  }
  if (user) {
    localStorage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(user));
  }
  if (expiresIn) {
    const expiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, expiryTime.toString());
  }
}

/**
 * Alle Tokens und Session-Daten löschen
 * @param {string} [reason] - Grund für das Löschen (Debug-Logging)
 */
export function clearTokens(reason) {
  const hadRefresh = !!localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.USER_DATA);
  localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
  if (hadRefresh) {
    console.warn('🔐 clearTokens aufgerufen:', reason || 'unbekannt', new Error().stack?.split('\n').slice(1, 4).join(' ← '));
  }
}

/**
 * Prüft ob ein gültiger JWT gespeichert ist
 * Prüft direkt den JWT exp-Wert (zuverlässiger als TOKEN_EXPIRY in localStorage)
 */
export function isLoggedIn() {
  const token = getAccessToken();
  if (!token) return Boolean(getRefreshToken());

  // Primär: JWT exp-Feld direkt prüfen (1 Min Puffer)
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp) {
      if (payload.exp * 1000 > Date.now() + 60000) return true;
      return Boolean(getRefreshToken());
    }
  } catch {
    // JWT kann nicht geparst werden — Fallback
  }

  // Fallback: TOKEN_EXPIRY aus localStorage
  const expiry = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
  if (expiry) {
    return Date.now() < (parseInt(expiry, 10) - 60000);
  }

  // Letzter Fallback: Token vorhanden = eingeloggt (Refresh wird bei 401 getriggert)
  return true;
}

// ============================================================
// Auth-Funktionen
// ============================================================

/** Flag um parallele Refresh-Calls zu verhindern */
let _refreshPromise = null;

/**
 * Login mit Google ID Token → Server gibt JWT zurück
 * 
 * @param {string} idToken - Google ID Token
 * @param {string} [accessToken] - Google Drive Access Token
 * @param {string} [refreshToken] - Google Drive Refresh Token
 * @param {string} [deviceInfo] - Geräte-Info
 * @returns {Promise<{user, accessToken, refreshToken}>}
 */
export async function loginWithGoogle(idToken, accessToken, refreshToken, deviceInfo) {
  const response = await fetch(`${SERVER_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      idToken,
      accessToken,
      refreshToken,
      deviceInfo: deviceInfo || navigator.userAgent,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || tGlobal('ui.svc.loginFailed'));
  }

  const data = await response.json();
  
  // Tokens speichern
  setTokens(data.accessToken, data.refreshToken, data.user, data.expiresIn);
  
  console.log('🔐 Server-Login erfolgreich:', data.user.email);
  
  return data;
}

/**
 * JWT Access Token erneuern via Refresh Token
 * Wird automatisch bei 401 aufgerufen.
 * 
 * @returns {Promise<string>} Neuer Access Token
 */
export async function refreshAccessToken() {
  // Verhindere parallele Refresh-Calls
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    
    if (!refreshToken) {
      throw new Error(tGlobal('ui.svc.noRefreshToken'));
    }

    let response;
    try {
      response = await fetch(`${SERVER_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
    } catch (networkError) {
      // Netzwerkfehler → Tokens NICHT löschen (temporärer Fehler)
      console.warn('🔄 Token-Refresh: Netzwerkfehler (Tokens bleiben erhalten):', networkError.message);
      throw new Error(tGlobal('ui.svc.refreshNetworkFailed'));
    }

    if (!response.ok) {
      // Nur bei definitiver Ablehnung (401/403) Tokens löschen
      // Bei Server-Fehlern (5xx) Tokens behalten für nächsten Versuch
      if (response.status === 401 || response.status === 403) {
        // 🧯 Das Gerät wird jetzt abgemeldet — melden, BEVOR die Tokens weg sind (sonst fehlt das Konto)
        reportError('sitzung_beendet', 'Erneuerung abgelehnt (HTTP ' + response.status + ')');
        clearTokens('Refresh Token vom Server abgelehnt (HTTP ' + response.status + ')');
        throw new Error(tGlobal('ui.svc.sessionExpired'));
      }
      console.warn('🔄 Token-Refresh: Server-Fehler', response.status, '(Tokens bleiben erhalten)');
      throw new Error(tGlobal('ui.svc.refreshServerFailed', { status: response.status }));
    }

    const data = await response.json();
    
    // Neuen Access Token speichern (Refresh Token bleibt gleich)
    setTokens(data.accessToken, null, null, data.expiresIn);
    
    console.log('🔄 JWT Access Token erneuert');
    return data.accessToken;
  })();

  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

/**
 * Logout — Server-Session beenden
 */
export async function serverLogout() {
  const refreshToken = getRefreshToken();
  
  // Server-Logout (best-effort, nicht-blockierend)
  if (refreshToken) {
    fetch(`${SERVER_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => {}); // Fehler ignorieren
  }
  
  clearTokens('Expliziter Logout');
  console.log('🔐 Server-Logout erfolgreich');
}

/**
 * Frischen Google Drive Access Token vom Server holen
 * Server nutzt gespeicherten Google Refresh Token → kein erneuter Login nötig!
 * 
 * @returns {Promise<string>} Google Drive Access Token
 */
export async function getDriveToken() {
  const response = await apiFetch('/auth/drive-token');
  return response.accessToken;
}

/**
 * Speichert einen client-seitig erhaltenen Drive Access Token auf dem Server.
 * Wird nach GIS-Popup verwendet, damit der Server den Token für ~55min cachen kann.
 * 
 * @param {string} accessToken - Google Drive Access Token
 * @returns {Promise<void>}
 */
export async function storeDriveToken(accessToken) {
  await apiFetch('/auth/store-drive-token', {
    method: 'POST',
    body: JSON.stringify({ accessToken }),
  });
}

/**
 * Tauscht einen Google Authorization Code gegen Access + Refresh Token.
 * Server tauscht den Code bei Google und speichert den Refresh Token dauerhaft.
 *
 * @param {string} code - Google Authorization Code
 * @param {string} [redirectUri] - redirect_uri für den Token-Tausch.
 *   Web/GIS initCodeClient → 'postmessage' (Default wenn weggelassen).
 *   Android Firebase Google Sign-In serverAuthCode → '' (leerer String).
 * @returns {Promise<{accessToken: string, hasRefreshToken: boolean}>}
 */
export async function exchangeDriveCode(code, redirectUri) {
  const body = { code };
  if (redirectUri !== undefined) body.redirectUri = redirectUri;
  return await apiFetch('/auth/exchange-code', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================================
// HTTP-Client mit Auto-Refresh
// ============================================================

/**
 * Authentifizierter API-Fetch mit automatischem Token-Refresh
 * 
 * @param {string} path - API-Pfad (z.B. '/packages')
 * @param {Object} [options] - Fetch-Optionen
 * @param {boolean} [_isRetry] - Intern: verhindert endlose Retry-Loops
 * @returns {Promise<any>} JSON-Antwort
 */
export async function apiFetch(path, options = {}, _isRetry = false) {
  let token = getAccessToken();
  if (!_isRetry && (!token || _sseTokenAbgelaufen(token)) && getRefreshToken()) {
    try { token = await refreshAccessToken(); } catch { token = getAccessToken(); }
  }
  
  const url = path.startsWith('http') ? path : `${SERVER_URL}${path}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Timeout via AbortController
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    options.timeout || REQUEST_TIMEOUT
  );

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeout);

    // 401 + TOKEN_EXPIRED → Token refreshen und Retry
    if (response.status === 401 && !_isRetry) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = {};
      }

      // Nur session-expired feuern wenn tatsächlich ein Token vorhanden war
      const hadToken = !!token;

      // Bei JEDEM 401 (egal welcher code) erst Refresh versuchen
      if (hadToken) {
        try {
          await refreshAccessToken();
          // Retry mit neuem Token
          return apiFetch(path, options, true);
        } catch (refreshError) {
          // Refresh endgültig fehlgeschlagen → Session beenden
          // (clearTokens wurde ggf. schon in refreshAccessToken aufgerufen)
          if (getRefreshToken()) {
            // Tokens noch da = temporärer Fehler, NICHT löschen
            console.warn('🔐 401 + Refresh-Fehler (temporär), Tokens bleiben:', refreshError.message);
            throw refreshError;
          }
          // Tokens bereits gelöscht → session-expired auslösen
          window.dispatchEvent(new CustomEvent('kiosk:session-expired'));
          throw refreshError;
        }
      }

      // Kein Token vorhanden → einfach Fehler werfen (kein clearTokens nötig)
      throw new Error(errorData.error || tGlobal('ui.svc.unauthorized'));
    }

    // Andere Fehler
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    // Leere Antwort (204 No Content)
    if (response.status === 204) {
      return null;
    }

    return response.json();
  } catch (err) {
    clearTimeout(timeout);

    // Timeout → lesbare Fehlermeldung (Kennzeichen: Anfrage kann beim Server angekommen sein → NICHT blind wiederholen)
    if (err.name === 'AbortError') {
      const e = new Error(tGlobal('ui.svc.serverTimeout'));
      e.zeitueberschreitung = true;
      throw e;
    }

    // Netzwerk-Fehler → lesbare Fehlermeldung (Kennzeichen: Server nicht erreicht → Wiederholen ist sicher)
    if (err.message === 'Failed to fetch' || err.message === 'Network request failed' || err.name === 'TypeError') {
      const e = new Error(tGlobal('ui.svc.noServerConnection'));
      e.netzweg = true;
      throw e;
    }

    throw err;
  }
}

// ============================================================
// Convenience-Methoden
// ============================================================

export const api = {
  /**
   * GET-Request
   * @param {string} path - API-Pfad
   * @param {Object} [params] - Query-Parameter
   */
  get: (path, params) => {
    if (params) {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
      ).toString();
      if (query) path += `?${query}`;
    }
    return apiFetch(path, { method: 'GET' });
  },

  /**
   * POST-Request
   * @param {string} path - API-Pfad
   * @param {any} data - Request-Body
   */
  post: (path, data) => apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  /**
   * PUT-Request
   * @param {string} path - API-Pfad
   * @param {any} data - Request-Body
   */
  put: (path, data) => apiFetch(path, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  /**
   * PATCH-Request
   * @param {string} path - API-Pfad
   * @param {any} data - Request-Body
   */
  patch: (path, data) => apiFetch(path, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  /**
   * DELETE-Request
   * @param {string} path - API-Pfad
   * @param {Object} [params] - Query-Parameter
   */
  delete: (path, params) => {
    if (params) {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
      ).toString();
      if (query) path += `?${query}`;
    }
    return apiFetch(path, { method: 'DELETE' });
  },
};

// ============================================================
// SSE (Server-Sent Events) für Echtzeit-Updates
// ============================================================

// ============================================================================
// Geteilte SSE-Verbindung (Multiplex)
// ============================================================================
// Früher öffnete JEDER Aufruf von connectSSE eine eigene EventSource. Auf der
// Paketseite liefen dadurch dauerhaft ZWEI parallel: App.js horcht global auf
// 'station:reload', AddPackageWithAI auf die Paket-Events. Folgen: doppelte
// Streams pro Gerät und ein doppelt gezählter `devices`-Wert in /station/ping —
// woraus die Kamera-Station ableitet, wie viele Tablets erreichbar sind.
//
// Jetzt teilen sich alle Aufrufer eine Verbindung: Sie wird beim ersten
// Abonnenten geöffnet und beim letzten geschlossen. Reconnect, Token-Erneuerung
// und Backoff liegen damit ebenfalls nur noch einmal vor.

const _sseAbonnenten = new Set(); // Set<Object> — je Aufrufer die Handler-Map
let _sseSource = null;
let _sseGebunden = new Set();     // Event-Namen, die an der aktuellen Source hängen
let _sseTimer = null;
let _sseRetry = 0;
let _sseForceRefresh = false;
let _sseLastEvent = 0;   // Zeitstempel des zuletzt empfangenen Server-Events (inkl. ping)
let _sseWatchdog = null; // Intervall-Wächter gegen stille „Zombie"-Verbindungen
let _sseVerbindet = false; // synchroner In-Flight-Guard: verhindert doppelte EventSource im await-Fenster
const SSE_MAX_RETRIES = 5;

/** Prüft anhand des exp-Feldes, ob der JWT abgelaufen ist (30 s Puffer). */
function _sseTokenAbgelaufen(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now() + 30000;
  } catch {
    return true; // nicht lesbar → als abgelaufen behandeln
  }
}

async function _sseFrischerToken() {
  const currentToken = getAccessToken();

  // Fall 1: Kein Token vorhanden
  if (!currentToken) {
    const rt = getRefreshToken();
    if (!rt) return null;
    try {
      console.log('🔌 SSE: Kein Access Token — refreshe...');
      return await refreshAccessToken();
    } catch (e) {
      console.warn('🔌 SSE: Token-Refresh fehlgeschlagen:', e.message);
      if (!getRefreshToken()) {
        console.warn('🔌 SSE: Session endgültig verloren — session-expired');
        window.dispatchEvent(new CustomEvent('kiosk:session-expired'));
      }
      return null;
    }
  }

  // Fall 2: Force-Refresh nach SSE-Fehler ODER Token tatsächlich abgelaufen
  if (_sseForceRefresh || _sseTokenAbgelaufen(currentToken)) {
    _sseForceRefresh = false;
    const rt = getRefreshToken();
    if (!rt) return null;
    try {
      console.log('🔌 SSE: Token abgelaufen/ungültig — refreshe...');
      const newToken = await refreshAccessToken();
      console.log('🔌 SSE: Token erfolgreich erneuert ✅');
      return newToken;
    } catch (e) {
      console.warn('🔌 SSE: Token-Refresh fehlgeschlagen:', e.message);
      if (!getRefreshToken()) {
        console.warn('🔌 SSE: Session endgültig verloren — session-expired');
        window.dispatchEvent(new CustomEvent('kiosk:session-expired'));
      }
      return getAccessToken(); // temporärer Fehler → alten Token weiternutzen
    }
  }

  // Fall 3: Token vorhanden und gültig
  return currentToken;
}

/** Reicht ein eingetroffenes Event an alle Abonnenten weiter, die es angemeldet haben. */
function _sseVerteilen(eventName, rohdaten) {
  _sseLastEvent = Date.now(); // Lebenszeichen: es kam gerade ein Event an
  let daten;
  try {
    daten = JSON.parse(rohdaten);
  } catch (e) {
    console.warn(`🔌 SSE: Parse-Fehler bei "${eventName}":`, e);
    return;
  }
  // Kopie, damit ein Abonnent sich während der Zustellung abmelden darf
  for (const handlerMap of [..._sseAbonnenten]) {
    const h = handlerMap[eventName];
    if (typeof h !== 'function') continue;
    try {
      h(daten);
    } catch (e) {
      console.warn(`🔌 SSE: Handler-Fehler bei "${eventName}":`, e);
    }
  }
}

/** Hängt einen Listener für einen Event-Namen an die aktuelle Source (einmalig). */
function _sseBinden(eventName) {
  if (!_sseSource || _sseGebunden.has(eventName)) return;
  _sseGebunden.add(eventName);
  _sseSource.addEventListener(eventName, (event) => _sseVerteilen(eventName, event.data));
}

/** Alle aktuell von Abonnenten gewünschten Event-Namen. */
function _sseAlleEventNamen() {
  const namen = new Set();
  for (const handlerMap of _sseAbonnenten) {
    for (const name of Object.keys(handlerMap)) namen.add(name);
  }
  return namen;
}

async function _sseVerbinden() {
  if (_sseAbonnenten.size === 0) return;   // niemand mehr da
  if (_sseSource || _sseVerbindet) return; // läuft ODER wird gerade aufgebaut (In-Flight-Guard)
  _sseVerbindet = true;
  try {
    const token = await _sseFrischerToken();
    if (!token) {
      console.warn('🔌 SSE: Kein gültiger Token — keine Verbindung');
      return;
    }
    if (_sseTokenAbgelaufen(token)) {
      console.warn('🔌 SSE: Token nach Refresh immer noch abgelaufen — stoppe');
      return;
    }
    // Während des await kann sich der letzte Abonnent abgemeldet ODER ein paralleler Pfad
    // bereits verbunden haben → sonst entstünde eine zweite, verwaiste EventSource.
    if (_sseAbonnenten.size === 0) return;
    if (_sseSource) return;

    const url = `${SERVER_URL}/events?token=${encodeURIComponent(token)}`;
    _sseSource = new EventSource(url);
    _sseGebunden = new Set();
    _sseLastEvent = Date.now();

    for (const name of _sseAlleEventNamen()) _sseBinden(name);

    // Benannter Server-„ping" (alle 15s) — direktes Lebenszeichen für den Zombie-Wächter.
    _sseSource.addEventListener('ping', () => { _sseLastEvent = Date.now(); });

    // 📡 Echtzeit-Lagerscanner-Events (Zebra TC75 / Ringscanner) als Browser-Event verteilen
    _sseSource.addEventListener('device:scan', (event) => {
      _sseLastEvent = Date.now();
      try {
        const detail = JSON.parse(event.data);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('kiosk:device-scan', { detail }));
        }
      } catch { /* ignore */ }
    });

    // 🖨️ Netzwerk-Druckaufträge (Druck-Brücke über gekoppeltes Bluetooth-Drucker-Handy)
    _sseSource.addEventListener('device:print-job', (event) => {
      _sseLastEvent = Date.now();
      try {
        const detail = JSON.parse(event.data);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('kiosk:device-print-job', { detail }));
        }
      } catch { /* ignore */ }
    });

    // 📱 Live-Geräte-Status (Handy-Verbindungen, Drucker-Kopplung im Konto)
    _sseSource.addEventListener('device:status', (event) => {
      _sseLastEvent = Date.now();
      try {
        const detail = JSON.parse(event.data);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('kiosk:device-status', { detail }));
        }
      } catch { /* ignore */ }
    });

    // 🎓 Admin-Fernsteuerung & Schulungsmodus (Befehle an Ziel-Handy)
    _sseSource.addEventListener('remote:control', (event) => {
      _sseLastEvent = Date.now();
      try {
        const detail = JSON.parse(event.data);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('kiosk:remote-control', { detail }));
        }
      } catch { /* ignore */ }
    });

    // 👁️ Live-Mirror-Frame vom Ziel-Handy an Admin-Board
    _sseSource.addEventListener('admin:remote-frame', (event) => {
      _sseLastEvent = Date.now();
      try {
        const detail = JSON.parse(event.data);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('kiosk:admin-remote-frame', { detail }));
        }
      } catch { /* ignore */ }
    });

    _sseSource.addEventListener('connected', () => {
      _sseRetry = 0;
      _sseForceRefresh = false;
      _sseLastEvent = Date.now();
      console.log(`🔌 SSE: Verbunden mit Server (Echtzeit-Events aktiv, ${_sseAbonnenten.size} Abonnent(en))`);
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('kiosk:sse-connected'));
        }
      } catch { /* ignore */ }
    });

    _sseStarteWatchdog();

    _sseSource.onerror = () => {
      if (!_sseSource) return;
      try { _sseSource.close(); } catch { /* */ }
      _sseSource = null;
      _sseGebunden = new Set();

      if (_sseAbonnenten.size === 0) return; // niemand mehr da → nicht neu aufbauen

      _sseRetry++;
      if (_sseRetry > SSE_MAX_RETRIES) {
        // NICHT dauerhaft aufgeben: langsamen Dauer-Reconnect (60 s) planen und den Zähler
        // zurücksetzen. Ein reines Tablet (App.js abonniert nur, ohne online/visibility-Handler)
        // bliebe sonst bis Seiten-Reload ohne Echtzeit-Events.
        console.warn(`🔌 SSE: ${SSE_MAX_RETRIES} Reconnect-Versuche fehlgeschlagen — langsamer Dauer-Reconnect (60 s)`);
        _sseRetry = 0;
        _sseForceRefresh = true;
        _sseTimer = setTimeout(() => { _sseTimer = null; _sseVerbinden(); }, 60000);
        return;
      }
      _sseForceRefresh = true; // bei Fehler immer Token erneuern
      const delay = Math.min(3000 * Math.pow(2, _sseRetry - 1), 60000);
      console.warn(`🔌 SSE: Verbindung verloren, Reconnect #${_sseRetry} in ${delay / 1000}s (Token wird erneuert)...`);
      _sseTimer = setTimeout(() => { _sseTimer = null; _sseVerbinden(); }, delay);
    };
  } finally {
    _sseVerbindet = false;
  }
}

/**
 * Zombie-Wächter: Eine EventSource kann still sterben — readyState bleibt OPEN,
 * es kommt aber nichts mehr an, und `onerror` feuert NICHT (auf Android nach Doze/
 * Netzwechsel typisch). Der Server sendet alle 15 s einen benannten „ping". Bleibt
 * jedes Server-Event >50 s aus, obwohl die Source „offen" ist und Abonnenten warten,
 * gilt die Verbindung als tot und wird hart neu aufgebaut. Schützt ALLE connectSSE-
 * Nutzer (Haupt-App, Tablet/Bench, Kamera-Station) an einer Stelle.
 */
function _sseStarteWatchdog() {
  if (_sseWatchdog) return;
  _sseWatchdog = setInterval(() => {
    if (_sseAbonnenten.size === 0) return; // keine Abonnenten → nichts zu tun
    if (!_sseSource) {
      // Verbindung ist tot (null) und kein Reconnect geplant/läuft → wiederbeleben.
      // Fängt auch den Fall ab, dass der Reconnect nach Retry-Erschöpfung aussetzt.
      if (!_sseTimer && !_sseVerbindet) _sseVerbinden();
      return;
    }
    if (_sseLastEvent && Date.now() - _sseLastEvent > 50000) {
      console.warn('🔌 SSE: >50 s kein Server-Event trotz offener Verbindung — Zombie, erzwinge Reconnect');
      forceReconnectSSE();
    }
  }, 20000);
}

/**
 * Erzwingt einen ECHTEN Neuaufbau der geteilten SSE-Verbindung.
 *
 * Nötig, wenn die bestehende EventSource zwar als „offen" gilt (readyState OPEN),
 * aber in Wirklichkeit still gestorben ist — ein „Zombie": es kommen keine Events
 * mehr an, ohne dass `onerror` feuert (auf Android nach Doze/Netzwechsel häufig).
 * Bloßes Re-Abonnieren (connectSSE erneut) half NICHT, weil connectSSE eine vorhandene
 * (Zombie-)Source wiederverwendet und nie ersetzt. Diese Funktion schließt die alte
 * Source hart und baut eine frische auf; die Abonnenten bleiben erhalten und werden
 * automatisch neu gebunden.
 */
export function forceReconnectSSE() {
  if (_sseTimer) { clearTimeout(_sseTimer); _sseTimer = null; }
  if (_sseSource) { try { _sseSource.close(); } catch { /* */ } }
  _sseSource = null;
  _sseGebunden = new Set();
  _sseRetry = 0;
  _sseForceRefresh = true; // Token sicherheitshalber erneuern
  _sseVerbinden();
}

/**
 * Abonniert Echtzeit-Events des Servers.
 *
 * Der Server sendet Named Events (event: package:created\ndata: {...}\n\n).
 * EventSource.onmessage fängt nur Events OHNE event-Feld ab, für Named Events
 * braucht es addEventListener pro Event-Typ.
 *
 * Mehrere Aufrufe teilen sich EINE Verbindung (siehe Kommentar oben).
 *
 * @param {Object} eventHandlers - Map von Event-Namen zu Callbacks
 *   z.B. { 'package:created': (data) => {...}, 'live-search': (data) => {...} }
 * @returns {Function} close — dieses Abonnement beenden
 */
export function connectSSE(eventHandlers = {}) {
  const handlerMap = { ...eventHandlers };
  _sseAbonnenten.add(handlerMap);

  // Läuft die Verbindung schon, nur die neuen Event-Namen nachbinden
  if (_sseSource) {
    for (const name of Object.keys(handlerMap)) _sseBinden(name);
  } else {
    _sseRetry = 0;
    _sseVerbinden();
  }

  let beendet = false;
  return () => {
    if (beendet) return;
    beendet = true;
    _sseAbonnenten.delete(handlerMap);

    if (_sseAbonnenten.size > 0) return; // andere hören noch zu → Verbindung behalten

    if (_sseTimer) { clearTimeout(_sseTimer); _sseTimer = null; }
    if (_sseWatchdog) { clearInterval(_sseWatchdog); _sseWatchdog = null; } // Dauer-Timer stoppen (lief sonst am Kiosk ewig weiter)
    if (_sseSource) { try { _sseSource.close(); } catch { /* */ } }
    _sseSource = null;
    _sseGebunden = new Set();
    console.log('🔌 SSE: Verbindung geschlossen (kein Abonnent mehr)');
  };
}
